import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, Mic, BookOpen, Feather } from "lucide-react";

export default function LandingPage() {
  const { user } = useAuth();
  const ctaLink = user ? "/journal" : "/auth";

  return (
    <div data-testid="landing-page" className="min-h-screen" style={{ background: '#141210', color: '#f5f0e8' }}>
      {/* Nav */}
      <nav className="echo-nav">
        <span className="echo-logo">ECHO</span>
        <div className="flex items-center gap-4">
          {user ? (
            <Link to="/journal" data-testid="nav-journal-link" className="btn-ghost">Open Journal</Link>
          ) : (
            <>
              <Link to="/auth" data-testid="nav-signin-link" style={{ color: '#9a9590', fontSize: '0.9rem', textDecoration: 'none' }} className="hover:text-white transition-colors">Sign in</Link>
              <Link to="/auth" data-testid="nav-start-btn" className="btn-ghost">Start free</Link>
            </>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 pt-20">
        <div className="hero-glow" />
        <p className="section-label fade-in">Personal Memory Infrastructure</p>
        <h1 className="fade-in fade-in-delay-1" style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(2rem, 5vw, 3.75rem)', fontWeight: 400, lineHeight: 1.2, maxWidth: '700px', marginBottom: '1rem' }}>
          One day, your life<br />will be a story.
        </h1>
        <p className="fade-in fade-in-delay-2" style={{ color: '#9a9590', fontSize: '1.1rem', marginBottom: '2.5rem' }}>
          Most people never get to read it.
        </p>
        <div className="fade-in fade-in-delay-2" style={{ width: '40px', height: '1px', background: '#3a3530', margin: '0 auto 2.5rem' }} />
        <div className="fade-in fade-in-delay-3 space-y-2 mb-3" style={{ color: '#a09890', fontSize: '0.95rem' }}>
          <p>Photos capture <strong style={{ color: '#f5f0e8' }}>what happened.</strong></p>
          <p>Social media captures <strong style={{ color: '#f5f0e8' }}>what was shown.</strong></p>
          <p>Nothing captures how life actually <strong style={{ color: '#f5f0e8' }}>felt.</strong></p>
        </div>
        <p className="fade-in fade-in-delay-3" style={{ color: '#9a9590', marginBottom: '2.5rem', fontSize: '0.95rem' }}>Until now.</p>
        <Link to={ctaLink} data-testid="hero-cta-btn" className="btn-amber fade-in fade-in-delay-3">
          Begin your archive <ArrowRight size={16} />
        </Link>
        <p className="mt-3 fade-in fade-in-delay-3" style={{ color: '#6a6560', fontSize: '0.8rem' }}>Free to start. Just for yourself.</p>
      </section>

      {/* The Problem */}
      <section className="py-32 px-6" style={{ borderTop: '1px solid #1e1c19' }}>
        <div className="max-w-2xl mx-auto lg:mx-[15%]">
          <p className="section-label">The Problem</p>
          <h2 className="section-heading mb-8">Most of your life<br />is already forgotten.</h2>
          <div className="space-y-6" style={{ color: '#9a9590', lineHeight: 1.8, fontSize: '0.95rem' }}>
            <p>You've lived thousands of days. Most of them have dissolved. The feelings, the conversations, the small moments that shaped you — gone. Not because they weren't worth keeping. Because you had no way to keep them.</p>
            <p>Your grandmother's life. Your father's inner world. What your own parents were actually thinking in their twenties. You'll never know — because there was no ECHO.</p>
          </div>
          <p className="mt-8" style={{ fontFamily: "'Playfair Display', serif", color: '#c4956a', fontSize: '0.95rem', lineHeight: 1.7 }}>
            Memory is what makes you <strong style={{ color: '#f5f0e8' }}>you</strong>. And right now, most of it is leaking away.
          </p>
        </div>
      </section>

      {/* The Vision */}
      <section className="py-32 px-6" style={{ background: '#161412', borderTop: '1px solid #1e1c19' }}>
        <div className="max-w-2xl mx-auto lg:mx-[15%]">
          <p className="section-label">The Vision</p>
          <h2 className="section-heading mb-8">A living memoir.<br />Written every day.</h2>
          <div className="space-y-6" style={{ color: '#9a9590', lineHeight: 1.8, fontSize: '0.95rem' }}>
            <p>ECHO is a conversational AI that acts like a trusted friend and biographer — always curious, never judgemental, always there. A few minutes a day. A spoken conversation. That's it.</p>
            <p>Over time, it builds a full picture of who you are — your patterns, your relationships, your fears, your joy. It notices when the same theme resurfaces. It remembers what you said about the interview you were dreading, and asks how it went.</p>
            <p>Every Sunday, it writes a biography paragraph of your week. Every year, an annual chapter. One day, a complete memoir of your actual life — not the highlights reel. The real thing.</p>
          </div>
        </div>
      </section>

      {/* Quote Samples */}
      <section className="py-24 px-6" style={{ borderTop: '1px solid #1e1c19' }}>
        <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { quote: "It was a week lived at two speeds at once \u2014 relentless by day, tender by night.", date: "From a Monday in March" },
            { quote: "You kept circling back to that conversation with your sister. Not to resolve it. Just to understand it better.", date: "From a Thursday in July" },
            { quote: "Something shifted. It was small enough to miss, but ECHO caught it.", date: "From a Sunday in November" }
          ].map((item) => (
            <div key={item.date} className="text-center">
              <p style={{ fontFamily: "'Playfair Display', serif", color: '#e0dbd4', fontSize: '0.9rem', lineHeight: 1.7, marginBottom: '1rem' }}>"{item.quote}"</p>
              <p style={{ color: '#6a6560', fontSize: '0.75rem' }}>{item.date}</p>
            </div>
          ))}
        </div>
        <p className="text-center mt-10" style={{ color: '#6a6560', fontSize: '0.8rem' }}>Examples of what ECHO writes. Your archive will be your own.</p>
      </section>

      {/* How It Works */}
      <section className="py-32 px-6" style={{ background: '#161412', borderTop: '1px solid #1e1c19' }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <p className="section-label">How It Works</p>
            <h2 className="section-heading">Just talk. ECHO does the rest.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: <Mic size={20} />, num: "01", title: "Speak freely", desc: "Open ECHO, tap the mic. Talk about your day like you're talking to a friend. No prompts, no forms. ECHO asks the questions." },
              { icon: <BookOpen size={20} />, num: "02", title: "It builds memory", desc: "ECHO reads between the lines \u2014 themes, feelings, recurring worries and joys. It surfaces connections across weeks and months." },
              { icon: <Feather size={20} />, num: "03", title: "Your life becomes literature", desc: "Weekly biography paragraphs. Annual memoir chapters. A growing archive of who you actually were, written with warmth and care." }
            ].map((card) => (
              <div key={card.num} className="feature-card" data-testid={`feature-card-${card.num}`}>
                <div className="flex items-center justify-between mb-6">
                  <span style={{ color: '#c4956a' }}>{card.icon}</span>
                  <span style={{ fontFamily: "'Playfair Display', serif", color: '#3a3530', fontSize: '1.5rem' }}>{card.num}</span>
                </div>
                <h3 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500, color: '#f5f0e8', fontSize: '1.05rem', marginBottom: '0.75rem' }}>{card.title}</h3>
                <p style={{ color: '#9a9590', fontSize: '0.85rem', lineHeight: 1.7 }}>{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Progression */}
      <section className="py-32 px-6" style={{ borderTop: '1px solid #1e1c19' }}>
        <div className="max-w-3xl mx-auto">
          <h2 className="section-heading text-center mb-16">What happens over time</h2>
          <div className="space-y-8">
            {[
              { label: "Day 1", text: "You talk about what happened today. ECHO listens and asks the right questions." },
              { label: "Week 1", text: "ECHO writes your first biography paragraph. You read it and feel something unexpected." },
              { label: "Month 1", text: "You notice you've been circling the same theme for weeks. ECHO noticed first." },
              { label: "Month 6", text: "You have 60+ conversations in your archive. A record of who you were this year." },
              { label: "Year 1", text: "Your first annual chapter is written. A page from your memoir. A version of you preserved." },
              { label: "Year 10", text: "You hand your children something no one's ever had: your actual inner life. Not edited. Not curated. True." }
            ].map((step) => (
              <div key={step.label} className="flex gap-6 items-start" data-testid={`timeline-step-${step.label}`}>
                <div className="flex-shrink-0 w-20 text-right">
                  <span style={{ fontFamily: "'Playfair Display', serif", color: '#c4956a', fontSize: '0.85rem' }}>{step.label}</span>
                </div>
                <div style={{ width: '1px', background: '#2e2a26', flexShrink: 0, minHeight: '1.5rem' }} />
                <p style={{ color: '#9a9590', fontSize: '0.9rem', lineHeight: 1.7 }}>{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Privacy */}
      <section className="py-24 px-6 text-center" style={{ background: '#161412', borderTop: '1px solid #1e1c19' }}>
        <div className="max-w-xl mx-auto">
          <h3 className="section-heading mb-4" style={{ fontSize: '1.5rem' }}>Your archive. Yours only.</h3>
          <p style={{ color: '#9a9590', fontSize: '0.9rem', lineHeight: 1.8 }}>
            ECHO is private by design. Your conversations are encrypted and belong entirely to you. No ads, no data mining, no sharing. This is your personal memory infrastructure — not a social network.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 px-6 text-center relative" style={{ borderTop: '1px solid #1e1c19' }}>
        <div className="hero-glow" style={{ top: '60%' }} />
        <div className="waveform mx-auto mb-8">
          {[...Array(5)].map((_, i) => <div key={`wave-${i}`} className="waveform-bar" />)}
        </div>
        <h2 className="section-heading mb-3" style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)' }}>
          Your story is happening<br />right now.
        </h2>
        <p style={{ color: '#9a9590', fontSize: '1rem', marginBottom: '2rem' }}>Ten years from now, you'll wish you'd started today.</p>
        <Link to={ctaLink} data-testid="footer-cta-btn" className="btn-amber">
          Begin your archive <ArrowRight size={16} />
        </Link>
        <p className="mt-3" style={{ color: '#6a6560', fontSize: '0.8rem' }}>Free to start. No credit card.</p>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6" style={{ borderTop: '1px solid #1e1c19' }}>
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <span className="echo-logo" style={{ fontSize: '1rem' }}>ECHO</span>
          <p style={{ color: '#6a6560', fontSize: '0.75rem' }}>A place to remember. Just for yourself.</p>
          <div className="flex items-center gap-6" style={{ fontSize: '0.8rem', color: '#6a6560' }}>
            <Link to="/auth" className="hover:text-white transition-colors" style={{ textDecoration: 'none', color: 'inherit' }}>Sign in</Link>
            <Link to="/auth" className="hover:text-white transition-colors" style={{ textDecoration: 'none', color: 'inherit' }}>Create account</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
